import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTE_OPTIONS = [
  { value: "medico", label: "Medico (documento firmato)" },
  { value: "asl", label: "ASL (documento firmato)" },
];

/**
 * Carica un documento di cambio terapia firmato (medico/ASL).
 * L'upload resta nello storico deblistering e avvia l'estrazione proposte IA.
 */
export default function CambioTerapiaDialog({
  open,
  onOpenChange,
  fascicoloId,
  ospiteLabel = "",
  token,
  authHeaders,
  onSuccess,
}) {
  const [enteEmittente, setEnteEmittente] = useState("medico");
  const [firmato, setFirmato] = useState(false);
  const [cambioDettagli, setCambioDettagli] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const headers = authHeaders || (token ? { Authorization: `Bearer ${token}` } : null);

  const resetForm = () => {
    setEnteEmittente("medico");
    setFirmato(false);
    setCambioDettagli("");
    setNote("");
    setFile(null);
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) resetForm();
    onOpenChange?.(nextOpen);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!fascicoloId) {
      toast.error("Fascicolo ospite mancante");
      return;
    }
    if (!file) {
      toast.error("Allega il documento firmato dal medico o dall'ASL");
      return;
    }
    if (!enteEmittente) {
      toast.error("Seleziona l'ente emittente (medico o ASL)");
      return;
    }
    if (!firmato) {
      toast.error("Conferma che il documento è firmato");
      return;
    }
    if (!headers) {
      toast.error("Sessione non valida");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("tipo", "cambio_terapia");
      fd.append("fascicolo_id", fascicoloId);
      fd.append("file", file);
      fd.append("ente_emittente", enteEmittente);
      fd.append("firmato", "true");
      if (cambioDettagli.trim()) fd.append("cambio_dettagli", cambioDettagli.trim());
      if (note.trim()) fd.append("note", note.trim());

      await axios.post(`${API_URL}/api/deblistering/documenti/upload`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Cambio terapia caricato. Verifica le proposte da validare.");
      resetForm();
      onOpenChange?.(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento cambio terapia");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cambio terapia</DialogTitle>
          <DialogDescription>
            Allega il documento firmato dal medico o dall&apos;ASL.
            {ospiteLabel ? (
              <>
                {" "}
                Ospite: <strong>{ospiteLabel}</strong>.
              </>
            ) : null}{" "}
            Il file resta nello storico e viene analizzato per aggiornare la terapia.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">Documento firmato *</Label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
              required
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-800 hover:file:bg-amber-100"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Firmato da *</Label>
            <select
              value={enteEmittente}
              onChange={(e) => setEnteEmittente(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40"
            >
              {ENTE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={firmato}
              onChange={(e) => setFirmato(e.target.checked)}
              className="mt-1 rounded border-slate-300"
            />
            <span>
              Confermo che il file allegato è il documento di cambio terapia firmato dal medico o dall&apos;ASL.
            </span>
          </label>
          <div>
            <Label className="text-sm font-medium text-slate-700">Dettagli cambio (opzionale)</Label>
            <Textarea
              value={cambioDettagli}
              onChange={(e) => setCambioDettagli(e.target.value)}
              rows={3}
              placeholder="Es. sospeso Triatec, nuovo Ramipril 5mg mattina..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Note (opzionale)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Note interne..."
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={uploading}>
              Annulla
            </Button>
            <Button type="submit" disabled={uploading} className="bg-amber-600 hover:bg-amber-700">
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              {uploading ? "Caricamento..." : "Carica cambio terapia"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
